import { QueryClient } from '@tanstack/react-query';

import { isApiError } from '@/lib/api';

/**
 * Factory for the app's `QueryClient` defaults. SSR (`ssr.tsx`) calls this
 * directly to get a fresh client per request, so no query state leaks across
 * requests. The browser's single long-lived instance lives in
 * `lib/query-client.browser.ts`, not here — this module is also evaluated by
 * the SSR bundle, and a module-scope `export const queryClient = ...` here
 * would build a second, unused client on every SSR render.
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: {
            // 'always': offline, a request is attempted and fails instead of
            // pausing. The default ('online') holds a mutation and sends it
            // on its own when the network comes back — an emission POST the
            // user did not ask for at that moment.
            queries: {
                networkMode: 'always',
                // 'always' flips TanStack's refetchOnReconnect default to
                // false; a query that failed offline must retry when the
                // network is back.
                refetchOnReconnect: true,
                staleTime: 30_000,
                refetchOnWindowFocus: false,
                // Retry only network/5xx failures; a 4xx is a definite answer.
                retry: (failureCount, error) => {
                    if (isApiError(error) && error.status < 500) return false;
                    return failureCount < 2;
                },
            },
            mutations: {
                networkMode: 'always',
                retry: false,
            },
        },
    });
}
